// Claude: Admin sidebar restructured into submenus — Users, Others, Plans, Reports, Content 13-03-2026
import React, { useEffect, useState } from "react";
import { Menu } from "antd";
import {
  AiOutlineLogout,
  AiOutlineUser,
} from "react-icons/ai";
import {
  MdOutlineDashboard,
  MdOutlineCardMembership,
  MdOutlineAccountTree,
  MdOutlineEvent,
  MdOutlineUploadFile,
  MdOutlineCardGiftcard,
  MdOutlineBarChart,
  MdOutlineBusiness,
  MdOutlineAdminPanelSettings,
  MdOutlinePeopleAlt,
  MdOutlineCategory,
  MdOutlineListAlt,
  MdOutlineAssessment,
  MdOutlineMenuBook,
  MdOutlineFeedback,
} from "react-icons/md";
import { GiOrganigram } from "react-icons/gi";
import { LiaPersonBoothSolid } from "react-icons/lia";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Topbar from "../../Partials/Admin/Topbar";
import { Image, Text } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { useToastWrapper } from "../../../Wrapper/toastWrapper";
import { handleLogout } from "../../../Utils/logout";
import { Images } from "../../../Assets/images";

type Props = {
  children?: React.ReactNode;
};

// Claude: map pathname → menu item key 13-03-2026
const PATH_KEY_MAP: Record<string, string> = {
  "/admin": "1",
  "/admin/sellers": "4",
  "/admin/buyers": "5",
  "/admin/admin-users": "12",
  "/admin/industry": "7",
  "/admin/departments": "21", // Claude: 16-Apr-2026
  "/admin/companies": "11",
  "/admin/events": "3",
  "/admin/organizer": "2",
  "/admin/seller-subscriptions": "6",
  "/admin/buyer-subscriptions": "19", // Claude: buyer subscription plans 11-Apr-2026
  "/admin/reward-plans": "9",
  "/admin/upload-reports": "8",
  "/admin/stats": "10",
  "/admin/industry-stats": "13",
  "/admin/department-stats": "21", // Claude: 18-04-2026
  "/admin/tags-list": "22", // Claude: 04-05-2026
  "/admin/buyer-faq": "14",
  "/admin/seller-faq": "15",
  "/admin/ai-knowledge-common": "18",
  "/admin/ai-knowledge-buyers": "16",
  "/admin/ai-knowledge-sellers": "17",
  "/admin/seller-feedback": "19",
  "/admin/buyer-feedback": "20",
};

// Claude: map pathname → submenu key to auto-open on load 13-03-2026
const PATH_SUBMENU_MAP: Record<string, string> = {
  "/admin/sellers": "sub-users",
  "/admin/buyers": "sub-users",
  "/admin/admin-users": "sub-users",
  "/admin/industry": "sub-others",
  "/admin/departments": "sub-others", // Claude: 16-Apr-2026
  "/admin/companies": "sub-others",
  "/admin/events": "sub-others",
  "/admin/organizer": "sub-others",
  "/admin/seller-subscriptions": "sub-plans",
  "/admin/buyer-subscriptions": "sub-plans", // Claude: buyer subscription plans 11-Apr-2026
  "/admin/reward-plans": "sub-plans",
  "/admin/upload-reports": "sub-reports",
  "/admin/stats": "sub-reports",
  "/admin/industry-stats": "sub-reports",
  "/admin/department-stats": "sub-reports", // Claude: 18-04-2026
  "/admin/tags-list": "sub-reports", // Claude: 04-05-2026
  "/admin/buyer-faq": "sub-content",
  "/admin/seller-faq": "sub-content",
  "/admin/ai-knowledge-common": "sub-content",
  "/admin/ai-knowledge-buyers": "sub-content",
  "/admin/ai-knowledge-sellers": "sub-content",
  "/admin/seller-feedback": "sub-users",
  "/admin/buyer-feedback": "sub-users",
};

const iconStyle = "text-[#1C1B1F]";

const AdminLayout = (props: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state: any) => state);
  const [authState, setAuthState] = useState(false);
  const [selectedKey, setSelectedKey] = useState("1");
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  useEffect(() => {
    // Claude: startsWith fallback so edit/add pages highlight the parent list item 25-Apr-2026
    const path = location.pathname;
    const key = PATH_KEY_MAP[path]
      || (path.startsWith("/admin/sellers/") ? "4" : null)
      || (path.startsWith("/admin/buyers/") ? "5" : null)
      || "1";
    const sub = PATH_SUBMENU_MAP[path]
      || (path.startsWith("/admin/sellers/") ? "sub-users" : null)
      || (path.startsWith("/admin/buyers/") ? "sub-users" : null)
      || undefined;
    setSelectedKey(key);
    if (sub) setOpenKeys([sub]);
  }, [location.pathname]);

  useEffect(() => {
    if (user && user?.role === "Admin") {
      setAuthState(true);
    } else {
      navigate("/admin/login");
    }
  }, [user]);

  const handleSignout = () => {
    handleLogout("/admin/login");
  };

  const sidebarWidth = "200px";

  return (
    <div className="flex">
      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={openKeys}
        onOpenChange={(keys) => setOpenKeys(keys as string[])}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: sidebarWidth,
          height: "100dvh",
          overflowY: "auto",
          backgroundColor: "#F1F1F1",
          border: 0,
          display: "flex",
          flexDirection: "column",
          gap: "0px",
          zIndex: 100,
        }}
      >
        {/* Logo */}
        <div className="flex h-[60px] items-center justify-center px-2">
          <Image src={Images.AftrLogo} maxH="44px" objectFit="contain" />
        </div>

        {/* Dashboard */}
        {/* Claude: Link wrapping enables right-click "open in new tab" 05-05-2026 */}
        <Menu.Item
          key="1"
          icon={<MdOutlineDashboard size={18} className={iconStyle} />}
          style={{ backgroundColor: "transparent", color: "black", margin: "2px 0", width: "100%", border: location.pathname === "/admin" ? "1px solid #050A30" : "none" }}
        >
          <Link to="/admin" style={{ color: "inherit", textDecoration: "none" }}>
            <Text fontSize="sm" fontWeight={500}>Dashboard</Text>
          </Link>
        </Menu.Item>

        {/* Users */}
        <Menu.SubMenu
          key="sub-users"
          icon={<MdOutlinePeopleAlt size={18} className={iconStyle} />}
          title={<Text fontSize="sm" fontWeight={500}>Users</Text>}
        >
          <Menu.Item key="4" icon={<LiaPersonBoothSolid size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/sellers" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/sellers" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Sellers</Text>
            </Link>
          </Menu.Item>
          <Menu.Item key="5" icon={<AiOutlineUser size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/buyers" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/buyers" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Buyers</Text>
            </Link>
          </Menu.Item>
          <Menu.Item key="12" icon={<MdOutlineAdminPanelSettings size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/admin-users" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/admin-users" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Admin Users</Text>
            </Link>
          </Menu.Item>
          {/* Claude: Feedback pages — seller and buyer, under Users submenu 26-03-2026 */}
          <Menu.Item key="19" icon={<MdOutlineFeedback size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/seller-feedback" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/seller-feedback" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Seller Feedback</Text>
            </Link>
          </Menu.Item>
          <Menu.Item key="20" icon={<MdOutlineFeedback size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/buyer-feedback" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/buyer-feedback" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Buyer Feedback</Text>
            </Link>
          </Menu.Item>
        </Menu.SubMenu>

        {/* Others */}
        <Menu.SubMenu
          key="sub-others"
          icon={<MdOutlineCategory size={18} className={iconStyle} />}
          title={<Text fontSize="sm" fontWeight={500}>Others</Text>}
        >
          <Menu.Item key="7" icon={<MdOutlineAccountTree size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/industry" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/industry" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Industry</Text>
            </Link>
          </Menu.Item>
          {/* Claude: Departments admin page 16-Apr-2026 */}
          <Menu.Item key="21" icon={<MdOutlineListAlt size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/departments" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/departments" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Departments</Text>
            </Link>
          </Menu.Item>
          <Menu.Item key="11" icon={<MdOutlineBusiness size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/companies" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/companies" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Companies</Text>
            </Link>
          </Menu.Item>
          <Menu.Item key="3" icon={<MdOutlineEvent size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/events" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/events" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Events</Text>
            </Link>
          </Menu.Item>
          <Menu.Item key="2" icon={<GiOrganigram size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/organizer" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/organizer" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Organizer</Text>
            </Link>
          </Menu.Item>
        </Menu.SubMenu>

        {/* Plans */}
        <Menu.SubMenu
          key="sub-plans"
          icon={<MdOutlineListAlt size={18} className={iconStyle} />}
          title={<Text fontSize="sm" fontWeight={500}>Plans</Text>}
        >
          {/* Claude: height+whiteSpace on Menu.Item needed — Ant Design enforces nowrap/overflow:hidden on item container 08-Apr-2026 */}
          <Menu.Item key="6" icon={<MdOutlineCardMembership size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/seller-subscriptions" ? "1px solid #050A30" : "none", height: "auto", whiteSpace: "normal" }}>
            <Link to="/admin/seller-subscriptions" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm" lineHeight="1">Seller Subscription Plans</Text>
            </Link>
          </Menu.Item>
          {/* Claude: buyer subscription plans nav item 11-Apr-2026 */}
          <Menu.Item key="19" icon={<MdOutlineCardMembership size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/buyer-subscriptions" ? "1px solid #050A30" : "none", height: "auto", whiteSpace: "normal" }}>
            <Link to="/admin/buyer-subscriptions" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm" lineHeight="1">Buyer Subscription Plans</Text>
            </Link>
          </Menu.Item>
          <Menu.Item key="9" icon={<MdOutlineCardGiftcard size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/reward-plans" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/reward-plans" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Reward Plans</Text>
            </Link>
          </Menu.Item>
        </Menu.SubMenu>

        {/* Reports */}
        <Menu.SubMenu
          key="sub-reports"
          icon={<MdOutlineAssessment size={18} className={iconStyle} />}
          title={<Text fontSize="sm" fontWeight={500}>Reports</Text>}
        >
          <Menu.Item key="8" icon={<MdOutlineUploadFile size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/upload-reports" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/upload-reports" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Uploads</Text>
            </Link>
          </Menu.Item>
          <Menu.Item key="10" icon={<MdOutlineBarChart size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/stats" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/stats" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Meeting Stats</Text>
            </Link>
          </Menu.Item>
          <Menu.Item key="13" icon={<MdOutlineBarChart size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/industry-stats" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/industry-stats" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Industry Stats</Text>
            </Link>
          </Menu.Item>
          {/* Claude: department stats 18-04-2026 */}
          <Menu.Item key="21" icon={<MdOutlineBarChart size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/department-stats" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/department-stats" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Department Stats</Text>
            </Link>
          </Menu.Item>
          {/* Claude: tags list 04-05-2026 */}
          <Menu.Item key="22" icon={<MdOutlineBarChart size={16} className={iconStyle} />}
            style={{ border: location.pathname === "/admin/tags-list" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/tags-list" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Tags List</Text>
            </Link>
          </Menu.Item>
        </Menu.SubMenu>

        {/* Content */}
        <Menu.SubMenu
          key="sub-content"
          icon={<MdOutlineMenuBook size={18} className={iconStyle} />}
          title={<Text fontSize="sm" fontWeight={500}>Content</Text>}
        >
          <Menu.Item key="14"
            style={{ border: location.pathname === "/admin/buyer-faq" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/buyer-faq" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Buyer FAQ</Text>
            </Link>
          </Menu.Item>
          <Menu.Item key="15"
            style={{ border: location.pathname === "/admin/seller-faq" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/seller-faq" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">Seller FAQ</Text>
            </Link>
          </Menu.Item>
          {/* Claude: AI KB — Common, Buyers, Sellers 25-03-2026 */}
          <Menu.Item key="18"
            style={{ border: location.pathname === "/admin/ai-knowledge-common" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/ai-knowledge-common" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">AI KB — Common</Text>
            </Link>
          </Menu.Item>
          <Menu.Item key="16"
            style={{ border: location.pathname === "/admin/ai-knowledge-buyers" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/ai-knowledge-buyers" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">AI KB — Buyers</Text>
            </Link>
          </Menu.Item>
          <Menu.Item key="17"
            style={{ border: location.pathname === "/admin/ai-knowledge-sellers" ? "1px solid #050A30" : "none" }}>
            <Link to="/admin/ai-knowledge-sellers" style={{ color: "inherit", textDecoration: "none" }}>
              <Text fontSize="sm">AI KB — Sellers</Text>
            </Link>
          </Menu.Item>
        </Menu.SubMenu>

        {/* Logout */}
        <Menu.Item
          key="logout"
          icon={<AiOutlineLogout size={18} className={iconStyle} />}
          onClick={handleSignout}
          style={{ backgroundColor: "transparent", color: "black", margin: "5px 0", width: "100%" }}
        >
          <Text fontSize="sm">Logout</Text>
        </Menu.Item>
      </Menu>

      <div className="grow" style={{ marginLeft: sidebarWidth }}>
        {authState && props.children}
      </div>
    </div>
  );
};

export default AdminLayout;
